<script setup lang="ts">
import { Eye, EyeOff, LogOut, Moon, PanelLeft, Search, Sun, User } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { Button, Input, Popover, Tooltip, ICON_STROKE } from '@/shared/ui'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

/**
 * Topbar 56 px: zbalenie sidebaru, hľadanie, prepínač cien, téma, profil.
 *
 * Prepínač Interné/Zákaznícke ceny je tu zámerne nápadný — partner ho zapína
 * pred zákazníkom a musí na prvý pohľad vidieť, v akom režime je
 * (handoff.md, riziko č. 4).
 */
const ui = useUiStore()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const themeIcon = computed(() => (ui.theme === 'dark' ? Moon : Sun))

function toggleTheme(): void {
	ui.theme = ui.theme === 'dark' ? 'light' : 'dark'
}

async function logout(): Promise<void> {
	await auth.logout()
	await router.push({ name: 'login' })
}

/**
 * Hľadanie v hlavičke.
 *
 * Nemá vlastnú obrazovku výsledkov — otvorí zoznam s predvyplneným filtrom.
 * Zoznamy už hľadať vedia a druhá implementácia toho istého by sa s nimi časom
 * rozišla; navyše je z adresy vidieť, čo sa hľadalo, a dá sa to poslať kolegovi.
 *
 * Hľadá sa tam, kde človek práve je: nad ponukami v ponukách, inde
 * v objednávkach. Kým sa hľadanie z detailu ponuky prepínalo do objednávok,
 * vyzeralo to, že ponuka s tým číslom neexistuje.
 */
const search = ref('')

const searchTarget = computed(() => (String(route.name ?? '').startsWith('quote') ? 'quotes' : 'orders'))

const searchLabelSk = computed(() =>
	searchTarget.value === 'quotes' ? 'Hľadať v ponukách' : 'Hľadať v objednávkach',
)

function submitSearch(): void {
	const query = search.value.trim()

	if (query === '') {
		return
	}

	void router.push({ name: searchTarget.value, query: { q: query } })
}

</script>

<template>
	<header class="flex h-(--topbar-h) items-center gap-2 border-b border-border bg-surface px-3">
		<Button
			variant="ghost"
			size="sm"
			:accessible-name-sk="ui.sidebarCollapsed ? 'Rozbaliť navigáciu' : 'Zbaliť navigáciu'"
			@click="ui.toggleSidebar()"
		>
			<PanelLeft :size="20" :stroke-width="ICON_STROKE" aria-hidden="true" />
		</Button>

		<div class="hidden min-w-0 flex-1 md:block">
			<label class="sr-only" for="topbar-search">{{ searchLabelSk }}</label>
			<div class="relative max-w-96">
				<Search
					:size="16"
					:stroke-width="ICON_STROKE"
					class="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-fg-subtle"
					aria-hidden="true"
				/>
				<Input
					id="topbar-search"
					v-model="search"
					type="search"
					size="sm"
					:placeholder-sk="`${searchLabelSk} — číslo alebo zákazník…`"
					class="pl-7"
					@keydown.enter="submitSearch"
				/>
			</div>
		</div>

		<div class="ml-auto flex items-center gap-1">
			<Tooltip
				:content-sk="ui.showsInternalPrices ? 'Skryť rabat a maržu pred zákazníkom' : 'Zobraziť interné ceny'"
				side="bottom"
			>
				<Button variant="ghost" size="sm" @click="ui.togglePriceMode()">
					<component
						:is="ui.showsInternalPrices ? Eye : EyeOff"
						:size="16"
						:stroke-width="ICON_STROKE"
						aria-hidden="true"
					/>
					<span class="hidden lg:inline">{{ ui.showsInternalPrices ? 'Interné ceny' : 'Zákaznícke ceny' }}</span>
				</Button>
			</Tooltip>

			<Button
				variant="ghost"
				size="sm"
				:accessible-name-sk="ui.theme === 'dark' ? 'Prepnúť na svetlý režim' : 'Prepnúť na tmavý režim'"
				@click="toggleTheme"
			>
				<component :is="themeIcon" :size="16" :stroke-width="ICON_STROKE" aria-hidden="true" />
			</Button>

			<Popover align="end" width="w-64" accessible-name-sk="Profil používateľa">
				<template #trigger>
					<Button variant="ghost" size="sm">
						<User :size="16" :stroke-width="ICON_STROKE" aria-hidden="true" />
						<span class="hidden max-w-40 truncate md:inline">{{ auth.user?.displayName ?? 'Profil' }}</span>
					</Button>
				</template>

				<div class="flex flex-col gap-2">
					<div class="flex flex-col">
						<span class="truncate text-base font-medium text-fg">{{ auth.user?.displayName ?? '—' }}</span>
						<span class="truncate text-xs text-fg-subtle">{{ auth.user?.email ?? '' }}</span>
					</div>
					<Button variant="secondary" size="sm" block @click="logout">
						<LogOut :size="16" :stroke-width="ICON_STROKE" aria-hidden="true" />
						Odhlásiť sa
					</Button>
				</div>
			</Popover>
		</div>
	</header>
</template>
